import { api } from "@/lib/api/client";
import type {
  LoginResponse,
  MeResponse,
  MfaSetupResponse,
  VerifyAmountsResponse,
} from "./types";

export const authApi = {
  login: async (email: string, password: string): Promise<LoginResponse> => {
    const { data } = await api.post<LoginResponse>("/auth/login/", { email, password });
    return data;
  },
  loginVerify: async (mfaToken: string, code: string): Promise<LoginResponse> => {
    const { data } = await api.post<LoginResponse>("/auth/login/verify/", {
      mfa_token: mfaToken,
      code,
    });
    return data;
  },
  mfaSetup: async (): Promise<MfaSetupResponse> => {
    const { data } = await api.post<MfaSetupResponse>("/auth/2fa/setup/");
    return data;
  },
  mfaEnable: async (code: string): Promise<{ backup_codes: string[] }> => {
    const { data } = await api.post<{ backup_codes: string[] }>("/auth/2fa/enable/", { code });
    return data;
  },
  mfaDisable: async (password: string): Promise<void> => {
    await api.post("/auth/2fa/disable/", { password });
  },
  mfaRegenerateBackupCodes: async (password: string): Promise<{ backup_codes: string[] }> => {
    const { data } = await api.post<{ backup_codes: string[] }>("/auth/2fa/backup-codes/", {
      password,
    });
    return data;
  },
  logout: async (): Promise<void> => {
    await api.post("/auth/logout/");
  },
  me: async (): Promise<MeResponse> => {
    const { data } = await api.get<MeResponse>("/auth/me/");
    return data;
  },
  updateProfile: async (payload: {
    full_name?: string;
    phone?: string;
    notification_prefs?: Record<string, boolean>;
  }): Promise<MeResponse> => {
    const { data } = await api.patch<MeResponse>("/auth/me/", payload);
    return data;
  },
  switchOrganisation: async (organisationId: string): Promise<void> => {
    await api.post("/auth/switch-org/", { organisation_id: organisationId });
  },
  /** « Toutes les cliniques » du propriétaire du compte (ADR 0065). */
  switchToAllClinics: async (): Promise<void> => {
    await api.post("/auth/switch-org/", { scope: "account" });
  },
  impersonate: async (userId: string): Promise<void> => {
    await api.post("/auth/impersonate/", { user_id: userId });
  },
  stopImpersonation: async (): Promise<void> => {
    await api.post("/auth/stop-impersonation/");
  },
  changePassword: async (oldPassword: string, newPassword: string): Promise<void> => {
    await api.post("/auth/change-password/", {
      old_password: oldPassword,
      new_password: newPassword,
    });
  },
  requestPasswordReset: async (email: string): Promise<void> => {
    await api.post("/auth/password-reset/request/", { email });
  },
  confirmPasswordReset: async (
    uid: string,
    token: string,
    newPassword: string,
    autoLogin = false,
  ): Promise<void> => {
    // auto_login=true (activation de compte) → le backend ouvre la session
    // (cookies JWT) du nouvel utilisateur.
    await api.post("/auth/password-reset/confirm/", {
      uid,
      token,
      new_password: newPassword,
      auto_login: autoLogin,
    });
  },
  /** Vérification d'adresse e-mail : lien reçu (uid + jeton dédié). Réponses :
   *  `verified`, `already_verified` (200) ou 400 `invalid_link`. */
  confirmEmailVerification: async (uid: string, token: string): Promise<{ code: string }> => {
    const { data } = await api.post<{ detail: string; code: string }>(
      "/auth/verify-email/confirm/",
      { uid, token },
    );
    return data;
  },
  /** Renvoi du lien de vérification à l'utilisateur connecté (borné 5/h). */
  resendEmailVerification: async (): Promise<{ code: string }> => {
    const { data } = await api.post<{ detail: string; code: string }>("/auth/verify-email/resend/");
    return data;
  },
  /**
   * Ré-affichage des montants (confidentialité) : re-saisie du mot de passe.
   * 401 = mot de passe incorrect, 403 = permission `finance.view_amounts`
   * absente (le bouton est déjà masqué côté UI dans ce cas).
   */
  verifyAmounts: async (password: string): Promise<VerifyAmountsResponse> => {
    const { data } = await api.post<VerifyAmountsResponse>("/auth/verify-amounts/", { password });
    return data;
  },
};
